import React, { useState, useContext } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../App';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  LayoutDashboard, 
  Database, 
  Users, 
  LogOut,
  Menu,
  X,
  Shield,
  Settings,
  Settings2
} from 'lucide-react';
import AdminDashboard from './AdminDashboard';
import AdminCollections from './AdminCollections';
import AdminUsers from './AdminUsers';
import AdminDropdownManagement from './AdminDropdownManagement';
import AdminSettings from './AdminSettings';

const menuItems = [
  { path: '', label: 'Dashboard', icon: LayoutDashboard },
  { path: 'collections', label: 'Collections', icon: Database },
  { path: 'users', label: 'Kullanıcılar', icon: Users },
  { path: 'dropdown-management', label: 'Dropdown Yönetimi', icon: Settings2 },
  { path: 'settings', label: 'Ayarlar', icon: Settings },
];

export default function UltraAdminPanel() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const currentPath = location.pathname.replace('/ultra-admin/panel/', '').replace('/ultra-admin/panel', '');

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className={`${
        sidebarOpen ? 'w-64' : 'w-20'
      } bg-gradient-to-b from-slate-900 to-slate-800 text-white transition-all duration-300 flex flex-col`}>
        {/* Header */}
        <div className="p-4 flex items-center justify-between border-b border-slate-700">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-purple-400" />
              <span className="font-bold text-lg">Ultra Admin</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white hover:bg-slate-700"
            data-testid="toggle-sidebar"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        {/* User info */}
        <div className="p-4 border-b border-slate-700">
          {sidebarOpen ? (
            <div>
              <p className="text-sm text-slate-400">Hoşgeldiniz</p>
              <p className="font-semibold truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          ) : (
            <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center mx-auto">
              <span className="text-sm font-bold">{user?.name?.charAt(0)}</span>
            </div>
          )}
        </div>

        {/* Menu */}
        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentPath === item.path;
              
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(`/ultra-admin/panel/${item.path}`)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive 
                      ? 'bg-purple-600 text-white' 
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                  data-testid={`menu-${item.path || 'dashboard'}`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
                </button>
              );
            })}
          </nav>
        </ScrollArea>

        {/* Logout */}
        <div className="p-4 border-t border-slate-700">
          <Button
            onClick={logout}
            variant="ghost"
            className={`w-full text-red-400 hover:text-red-300 hover:bg-slate-700 ${
              sidebarOpen ? 'justify-start' : 'justify-center'
            }`}
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span className="ml-3">Çıkış Yap</span>}
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Routes>
          <Route index element={<AdminDashboard />} />
          <Route path="collections" element={<AdminCollections />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="dropdown-management" element={<AdminDropdownManagement />} />
          <Route path="settings" element={<AdminSettings />} />
        </Routes>
      </main>
    </div>
  );
}